import { computed, inject, Injectable, Injector, signal } from '@angular/core';
import { handleRequestError } from '@softline/application';
import { MessageBarService, translator } from '@softline/ui-core';
import {
  MASTERDATA_PRODUCT,
  MasterdataProduct,
  MasterdataService,
} from '@softapps/core/masterdata';
import {
  MANUAL_MATCH,
  NO_MATCH,
  PositionMatch,
  TEXT_MATCH,
} from '../types/product-match';
import { PositionDraft } from '../types/working-copy';
import { ExtractionEditService } from './extraction-edit.service';
import { matchDraftFields, ProductLookup } from '../utils/product-match';

/** Parallel lookups per run — the masterdata query is not free for the ERP. */
const CONCURRENCY = 3;

/**
 * Resolves position drafts against the article masterdata and holds the
 * verdicts. The map is keyed by DRAFT IDENTITY, not index: reordering or
 * removing rows must not shuffle the matches, and a freshly added draft simply
 * has none yet.
 */
@Injectable()
export class ProductMatchService {
  private readonly masterdata = inject(MasterdataService);
  private readonly editService = inject(ExtractionEditService);
  private readonly messageBar = inject(MessageBarService);
  private readonly injector = inject(Injector);
  private readonly t = translator();

  private readonly _matches = signal<ReadonlyMap<PositionDraft, PositionMatch>>(
    new Map()
  );
  private readonly _running = signal(0);
  /** Drafts the user decided on — a re-run must not overwrite those. */
  private decided = new WeakSet<PositionDraft>();
  /** Invalidates in-flight runs after a `reset()` / a new document. */
  private runId = 0;

  readonly matches = this._matches.asReadonly();
  readonly matching = computed(() => this._running() > 0);
  /** A lookup is still out for at least one row — the export must wait for it. */
  readonly hasPending = computed(() =>
    [...this._matches().values()].some((match) => match.status === 'pending')
  );

  private readonly lookup: ProductLookup = {
    byKey: (key) =>
      this.masterdata.search<MasterdataProduct>(MASTERDATA_PRODUCT, null, {
        limit: 2,
        filter: { type: 'stringEquals', name: 'key', value: key },
      }),
    search: (term, limit) =>
      this.masterdata.search<MasterdataProduct>(MASTERDATA_PRODUCT, term, { limit }),
  };

  matchFor(draft: PositionDraft): PositionMatch {
    return this._matches().get(draft) ?? NO_MATCH;
  }

  /** Drops every verdict — call before matching a newly loaded document. */
  reset(): void {
    this.runId++;
    this.decided = new WeakSet<PositionDraft>();
    this._matches.set(new Map());
  }

  /**
   * Matches every draft the user has not decided on (`force` re-runs those
   * too). Lookup errors degrade the affected row to "no match" and are summed
   * up into ONE warning instead of a message bar storm.
   */
  async matchAll(
    drafts: readonly PositionDraft[],
    options?: { force?: boolean }
  ): Promise<void> {
    const pending = drafts.filter((draft) => options?.force || !this.decided.has(draft));
    if (!pending.length) return;

    const run = ++this.runId;
    this.setAll(pending, { status: 'pending' });
    this._running.update((n) => n + 1);
    let failed = 0;
    try {
      const queue = [...pending];
      const worker = async (): Promise<void> => {
        for (let draft = queue.shift(); draft; draft = queue.shift()) {
          if (run !== this.runId) return;
          if (!(await this.resolve(draft, run))) failed++;
        }
      };
      await Promise.all(
        Array.from({ length: Math.min(CONCURRENCY, queue.length) }, worker)
      );
    } finally {
      this._running.update((n) => n - 1);
    }
    if (failed && run === this.runId) this.warnFailed(failed);
  }

  /** Re-runs the lookup for a single draft (user asked for it — no skipping). */
  async matchOne(draft: PositionDraft): Promise<void> {
    const run = this.runId;
    this.decided.delete(draft);
    this.set(draft, { status: 'pending' });
    this._running.update((n) => n + 1);
    try {
      if (!(await this.resolve(draft, run))) this.warnFailed(1);
    } finally {
      this._running.update((n) => n - 1);
    }
  }

  /** Accepts a suggestion — it exports like an automatic match from now on. */
  confirm(draft: PositionDraft): void {
    const match = this.matchFor(draft);
    if (match.status !== 'suggested') return;
    this.decided.add(draft);
    this.set(draft, { status: 'matched', product: match.product, via: match.via });
  }

  assign(draft: PositionDraft, product: MasterdataProduct): void {
    this.decided.add(draft);
    this.set(draft, { status: 'matched', product, via: 'manual' });
  }

  /** Books the row on the supplier's Manueller Artikel — an explicit decision. */
  assignManual(draft: PositionDraft): void {
    this.decided.add(draft);
    this.set(draft, MANUAL_MATCH);
  }

  /** Exports the row as a text position without an article — an explicit decision. */
  assignText(draft: PositionDraft): void {
    this.decided.add(draft);
    this.set(draft, TEXT_MATCH);
  }

  /** Back to undecided — kept across re-runs, the user asked for the blank. */
  clear(draft: PositionDraft): void {
    this.decided.add(draft);
    this.set(draft, { status: 'none' });
  }

  /** False when the lookup itself failed (the row falls back to "no match"). */
  private async resolve(draft: PositionDraft, run: number): Promise<boolean> {
    try {
      const match = await matchDraftFields(draft.fields, this.lookup, {
        supplierName: this.editService.supplierName(),
      });
      if (run === this.runId) this.set(draft, match);
      return true;
    } catch (e) {
      handleRequestError(e, this.injector, { show: false });
      if (run === this.runId) this.set(draft, { status: 'none' });
      return false;
    }
  }

  private warnFailed(n: number): void {
    this.messageBar.warning(
      String(this.t('#AI_TABLE_EXTRACTOR.MATCH.SEARCH_FAILED', { n })())
    );
  }

  private set(draft: PositionDraft, match: PositionMatch): void {
    this._matches.update((matches) => new Map(matches).set(draft, match));
  }

  private setAll(drafts: readonly PositionDraft[], match: PositionMatch): void {
    this._matches.update((matches) => {
      const next = new Map(matches);
      for (const draft of drafts) next.set(draft, match);
      return next;
    });
  }
}
